"use client";

import React, { memo, useCallback } from "react";
import { formatDistanceToNow } from "date-fns";
import { MoreHorizontal, Pencil, Trash2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@wtc/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { UserDisplayPopover } from "@/components/shared/UserDisplayPopover";
import { LinkifiedText } from "@wtc/link-preview/react";
import type { EntityNoteBase } from "./types";

const getInitials = (firstName?: string | null, lastName?: string | null) => {
  const first = firstName?.charAt(0)?.toUpperCase() || "";
  const last = lastName?.charAt(0)?.toUpperCase() || "";
  return first + last || "?";
};

const noteTypeClass = (noteType: string) => {
  switch (noteType) {
    case "internal":
      return "bg-red-100 text-red-800 border-red-200";
    case "client":
      return "bg-blue-100 text-blue-800 border-blue-200";
    case "general":
    default:
      return "bg-muted text-foreground border-border";
  }
};

export interface NoteCommentRowProps {
  note: EntityNoteBase;
  onEdit: (note: EntityNoteBase) => void;
  onDelete: (noteId: string) => void;
  canEdit?: boolean;
}

/**
 * Single note/comment row with overflow menu — reusable when an entity maps to EntityNoteBase.
 */
export const NoteCommentRow = memo(function NoteCommentRow({
  note,
  onEdit,
  onDelete,
  canEdit = true,
}: NoteCommentRowProps) {
  const handleEdit = useCallback(() => onEdit(note), [note, onEdit]);
  const handleDelete = useCallback(() => onDelete(note.id), [note.id, onDelete]);
  const authorName = `${note.user?.first_name ?? ""} ${note.user?.last_name ?? ""}`.trim();
  const hasAuthor = Boolean(note.user?.id || note.user?.email);

  return (
    <div
      className={`rounded-lg border p-3 ${
        note.is_internal ? "border-red-200 bg-red-50/40" : "border-border bg-card"
      }`}
    >
      <div className="flex items-start gap-2">
        <Avatar className="h-8 w-8 shrink-0">
          <AvatarFallback className="text-xs bg-muted">
            {getInitials(note.user?.first_name, note.user?.last_name)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            {hasAuthor ? (
              <UserDisplayPopover
                user={{
                  id: note.user?.id ?? "unknown",
                  first_name: note.user?.first_name ?? undefined,
                  last_name: note.user?.last_name ?? undefined,
                  email: note.user?.email ?? undefined,
                }}
              >
                <button type="button" className="text-[13px] font-medium text-foreground hover:underline">
                  {authorName || note.user?.email || "Unknown user"}
                </button>
              </UserDisplayPopover>
            ) : (
              <span className="text-[13px] font-medium text-foreground">Unknown user</span>
            )}
            {note.is_internal && (
              <span className="inline-flex items-center gap-0.5 text-xs text-red-600">
                <Lock className="h-3 w-3" />
                Internal
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
            </span>
            {note.note_type && (
              <Badge variant="outline" className={`text-[10px] ${noteTypeClass(note.note_type)}`}>
                {note.note_type}
              </Badge>
            )}
          </div>
          {/* Note bodies used to render as raw text, so a pasted URL was dead
              characters. LinkifiedText escapes as before (it renders strings as
              React children) and adds rel="noopener noreferrer" — the referrer
              half matters, since a Hub URL carries the entity id. */}
          <LinkifiedText
            text={note.content}
            className="block text-[13px] text-foreground whitespace-pre-wrap break-words"
            linkClassName="text-primary underline underline-offset-2 hover:text-primary/80"
          />
          {note.updated_at !== note.created_at && (
            <p className="text-[11px] text-muted-foreground">
              Edited {formatDistanceToNow(new Date(note.updated_at), { addSuffix: true })}
            </p>
          )}
        </div>
        {canEdit && (
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 relative z-[2]"
                    aria-label="Note actions"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>Note actions</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end" sideOffset={6} className="z-[10001]">
              <DropdownMenuItem onClick={handleEdit}>
                <Pencil className="h-3.5 w-3.5 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem className="text-destructive" onClick={handleDelete}>
                <Trash2 className="h-3.5 w-3.5 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
});
